import InfoRounded from '@mui/icons-material/InfoRounded';
import Grid from '@mui/material/Grid';
import { AlertTooltip } from '@openx/components-core/lib/AlertTooltip/AlertTooltip.tsx';
import { type InventoryAndContentState, InventoryContentOption } from '@openx/types/targeting/inventoryAndContent.ts';
import { ListIconType } from '@openx/types/targeting/targeting.ts';
import { Criteria, type OptionEntriesProps } from '@openx/types/targeting/targetingValuesTypes.ts';
import { noop } from '@openx/utils/noop.ts';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { AnyAction } from 'redux';
import { mapTypeToIconType } from '../../constants.ts';
import { DimensionChip } from '../../shared/DimensionChip/DimensionChip.tsx';
import { DimensionChipLine } from '../../shared/DimensionChip/DimensionChipLine.tsx';
import { FiltersTitle } from '../../shared/FiltersTitle/FiltersTitle.tsx';
import { useInventoryAndContentLabels } from '../../shared/InventoryAndContent/constants.ts';
import {
  resetFilter,
  setComparisonOpValue,
  setContentCategoryOp,
} from '../../shared/InventoryAndContent/inventoryHelpers.ts';
import { useComparisonLabelForAppInventory } from '../../shared/InventoryAndContent/useComparisonLabelForAppInventory.ts';
import { SelectedItems } from '../../shared/SelectedItems/SelectedItems.tsx';
import { Switch } from '../../shared/Switch/Switch.tsx';
import type { TargetingItemsProps } from '../../types.ts';
import { getSelectedOptionsCount } from './utils.ts';

interface DeafaultItemsProps extends TargetingItemsProps<InventoryAndContentState> {
  option: typeof InventoryContentOption.APP_BUNDLE_ID | typeof InventoryContentOption.PAGE_URL;
  inventoryListCount?: number;
}

export const DefaultItems = ({
  readonly = true,
  dispatchInventory = noop,
  targetingParams,
  option,
  dataTest,
  inventoryListCount = 0,
  isNotLastItem = false,
  switchState: chipLineSwitchState,
  showDimensionChipLine = true,
  disabledReasonMessage,
}: DeafaultItemsProps) => {
  const { t } = useTranslation();
  const labels = useInventoryAndContentLabels();
  const { getComparisonLabel } = useComparisonLabelForAppInventory({ option });

  const handleChange = useCallback(
    (values: string[], inventoryContentOption: string, comparisonType: string) => {
      dispatchInventory({ payload: { comparisonType, values }, type: setComparisonOpValue[inventoryContentOption] });
    },
    [dispatchInventory]
  );

  const { op: dimension, val: optionsMap } = targetingParams[option];
  const { inventorylist_id } = targetingParams[InventoryContentOption.APP_BUNDLE_ID];
  const switchState = dimension === Criteria.ANY;
  const switchDisabled = Boolean(inventorylist_id && switchState && option === InventoryContentOption.APP_BUNDLE_ID);
  const optionsEntries: OptionEntriesProps[] = Object.entries(optionsMap ?? {});
  const isPageUrl = option === InventoryContentOption.PAGE_URL;

  return (
    <Grid key={option} data-test="sub-section">
      <FiltersTitle
        onClear={() =>
          dispatchInventory({
            payload: {
              contentOption: option,
            },
            type: resetFilter,
          } as unknown as AnyAction)
        }
        title={`${labels[option].label} [ ${getSelectedOptionsCount(optionsEntries, inventoryListCount)} ]`}
        readonly={readonly}
        disabledReasonMessage={disabledReasonMessage}
      />

      {!readonly && (
        <Grid container>
          <Grid size={2}>
            <Switch
              switchState={switchState}
              onSwitchChange={() =>
                dispatchInventory({
                  payload: switchState ? Criteria.ALL : Criteria.ANY,
                  type: setContentCategoryOp[option],
                } as unknown as AnyAction)
              }
              switchOffLabel={t('all')}
              switchOnLabel={t('any')}
              disabled={switchDisabled}
            />
          </Grid>

          {switchDisabled && (
            <Grid
              size={1}
              sx={{
                mt: 1,
              }}
            >
              <AlertTooltip
                icon={<InfoRounded />}
                color="default"
                title={t('When Inventory List is selected, then "All" criteria is not available.')}
              />
            </Grid>
          )}
        </Grid>
      )}

      {optionsEntries.map(([comparisonType, items], idx) =>
        items?.length ? (
          <Grid
            key={comparisonType}
            data-test={`section-items-${getComparisonLabel(comparisonType).toLowerCase().replaceAll(' ', '-')}`}
          >
            <SelectedItems
              filtersTitle={getComparisonLabel(comparisonType)}
              isAddIcon={mapTypeToIconType[comparisonType] === ListIconType.INCLUDE}
              selectedItems={items}
              readonly={readonly}
              handleChange={values => handleChange(values, option, comparisonType)}
              isRemovingAll={true}
              dataTest={dataTest}
            />
            {idx < optionsEntries.length - 1 && <DimensionChip targetingDimension={dimension} isInsideOption />}
            {idx === optionsEntries.length - 1 && isNotLastItem && showDimensionChipLine && isPageUrl && (
              <DimensionChipLine switchState={chipLineSwitchState} />
            )}
          </Grid>
        ) : null
      )}
    </Grid>
  );
};
